import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Bell,
  LayoutDashboard,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  Users,
  X,
} from "lucide-react";
import { Logo } from "../Logo";
import { ThemeToggle } from "../ThemeToggle";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const NAV = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
  { to: "/admin/clients", label: "Clients", icon: Users, testId: "admin-nav-clients" },
  { to: "/admin/notifications", label: "Notifications", icon: Bell, testId: "admin-nav-notifications" },
  { to: "/admin/audit", label: "Activity log", icon: ScrollText, testId: "admin-nav-audit" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testId: "admin-nav-settings" },
];

const linkClass = ({ isActive }) =>
  cn(
    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-primary/10 text-primary"
      : "text-muted-foreground hover:bg-accent hover:text-foreground"
  );

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const navBody = (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {NAV.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            className={linkClass}
            data-testid={testId}
            onClick={() => setOpen(false)}
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="mb-2 px-3">
          <p className="truncate text-sm font-medium">{user?.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground"
          onClick={handleLogout}
          data-testid="admin-logout-button"
        >
          <LogOut className="mr-3 h-4 w-4" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <aside className="surface-1 fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-border lg:flex">
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <Logo size="h-6" to="/admin/dashboard" />
          <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
            Admin
          </span>
        </div>
        {navBody}
      </aside>

      {open ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setOpen(false)}
            data-testid="admin-nav-overlay"
          />
          <aside className="surface-1 absolute inset-y-0 left-0 flex w-72 flex-col border-r border-border">
            <div className="flex h-14 items-center justify-between border-b border-border px-4">
              <Logo size="h-6" />
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} data-testid="admin-nav-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            {navBody}
          </aside>
        </div>
      ) : null}

      <div className="lg:pl-60">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-border bg-background px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen(true)}
              data-testid="admin-nav-open"
            >
              <Menu className="h-4 w-4" />
            </Button>
            <span className="lg:hidden">
              <Logo size="h-6" to="/admin/dashboard" />
            </span>
            <p className="hidden text-sm font-medium lg:block">Administration</p>
          </div>
          <ThemeToggle />
        </header>
        <main className="px-4 py-5 sm:px-6 sm:py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
